import React, { useEffect, useRef, useState } from 'react';

import { Box, Button, Paper, Popper, Stack, Typography } from '@mui/material';

import { PropertyResponse } from '~/services/AccommodationService';

import AccommodationPreviewLink from './AccommodationPreviewLink';

interface Props {
  property: PropertyResponse;
}

export default function AccommodationLinksPanel({ property }: Props) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => handleOpenChange(false), 600);
  };

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    if (!timeoutRef.current && open) {
      timeoutRef.current = setTimeout(() => handleOpenChange(false), 2500); // allow extra time on first open to avoid flickering
    }
    if (timeoutRef.current && !open) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  return (
    <Box>
      <Button ref={buttonRef} variant="outlined" size="small" color="primary" onClick={() => handleOpenChange(!open)}>
        Preview
      </Button>
      <Popper
        open={open}
        anchorEl={buttonRef.current}
        placement="bottom-end"
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
        ]}
      >
        <Paper
          elevation={6}
          sx={{
            padding: '1.2rem',
            width: 'min(30rem, calc(100vw - 2rem))',
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <Stack gap={2}>
            <Typography variant="h6">Links</Typography>
            {property.connectedOffers && property.connectedOffers.length > 0 ? (
              property.connectedOffers
                .sort((a, b) => {
                  if (a.isActive && !b.isActive) {
                    return -1;
                  }
                  if (!a.isActive && b.isActive) {
                    return 1;
                  }
                  return 0;
                })
                .map((offer, i) => (
                  <AccommodationPreviewLink
                    key={offer.ids.id || offer.idSalesforceExternal || 'link-index-' + i}
                    type={offer.offerType}
                    name={offer.name}
                    ids={offer.ids}
                    status={offer.isActive ? 'active' : 'inactive'}
                  />
                ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No connected offers
              </Typography>
            )}
          </Stack>
        </Paper>
      </Popper>
    </Box>
  );
}
